/**
 * CALGARY-CENTRE — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 17 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_CALGARY_CENTRE: Location[] = [
// ────────────────────────────────────────────────────────────── BELTLINE ──
  {
    slug: "beltline",
    name: "Beltline",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0392, lng: -114.0776 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Beltline sits about 10 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 5 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From above, the Beltline is a ruled page: 10th Avenue SW, 12th Avenue SW and 14th Avenue SW run east–west in strict parallel, and the short cross-streets deliver every one of them to the 17th Avenue SW retail strip at the district's southern edge.",
      consequence:
        "A condo or infill listing here is sold on its position in that grid — how it sits between the towers to the north and 17th Avenue to the south — and a ground-level camera standing in one block cannot show any of it.",
      resolution:
        "A rising reveal over the property, framed so the avenue grid and the 17th Avenue SW retail strip stay in view, puts the address in context. The CYYC authorisation is filed before the date is confirmed, and the work is delivered in the standard turnaround.",
    },
    landmarks: [
      "17th Avenue SW retail strip",
      "Central Memorial Park",
      "Barb Scott Park",
      "12th Avenue SW",
      "1st Street SW",
    ],
    terrain:
      "Flat terrace ground carrying one of the city's densest residential grids — mid-rise and high-rise blocks shoulder to shoulder, with Central Memorial Park and Haultain Park as the few green interruptions.",
    windNote:
      "Chinook belt. A winter chinook can swing conditions inside an hour, and between mid-rise blocks the gusts arrive from odd directions — the open-sky forecast understates what the airframe meets at rooftop height.",
    seasonalWindow:
      "Long summer evenings send raking light straight down the east–west avenues; in winter the low sun clears the mid-rise rooftops for only part of the day, so the middle of the day often carries the district's best light.",
    bylawNote:
      "Calgary's parks bylaws restrict drone takeoff and landing in municipal parks and on pathways — Central Memorial Park and Barb Scott Park included — so launches stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Scheduling is driven by the authorisation and the weather window, not the drive.",
    faqs: [
      {
        question: "Do you need airspace authorisation to fly a drone in the Beltline?",
        answer:
          "Yes. The Beltline sits about 10 km from Calgary International, inside the CYYC Class C control zone, so flights run under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the shoot date is confirmed. The specific site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "Can a Beltline condo shoot show 17th Avenue SW without flying over the street?",
        answer:
          "Yes. The avenues run parallel, so an orbit flown from the property's own block can hold the 17th Avenue SW retail strip in frame without overflying it. Flight paths are planned over the launch site and the subject, and the authorised altitude does the work of the wide view.",
      },
    ],
    primaryKeywords: [
      "drone photography Beltline",
      "aerial photography Beltline",
      "Beltline drone services",
    ],
    nearestLocations: ["downtown-calgary", "mission", "cliff-bungalow"],
  },

  // ───────────────────────────────────────────────────────────── CHINATOWN ──
  {
    slug: "chinatown",
    name: "Chinatown",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0501, lng: -114.0645 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Chinatown lies inside the charted CYYC control zone, about 9 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 5 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The geometry around Chinatown is tight in both directions: Calgary International's control zone is measured at about 9 km one way, and the Foothills Medical Centre heliport works about 5 km the other, so a flight plan here is bounded twice before the drone leaves its case.",
      consequence:
        "There is no room to improvise on the day — an altitude change or a drift toward the Centre Street Bridge that was not in the filed plan is not available, so the shot list has to be right before the paperwork goes in.",
      resolution:
        "The shot list is walked in advance, the NAV CANADA authorisation is filed to match it before the date is confirmed, and the flight flown is the flight planned — delivered in the standard turnaround.",
    },
    landmarks: [
      "Calgary Chinese Cultural Centre",
      "Centre Street Bridge",
      "Sien Lok Park",
      "Riverfront Avenue",
    ],
    terrain:
      "A compact riverfront district on flat ground where Centre Street meets the water — a tight cluster of blocks pressed between the river bank and the tower core immediately south.",
    windNote:
      "Chinook belt, with the added complication of the tower core to the south — gusts that cross downtown arrive here broken and shifty, and the open river corridor gives the west wind a clear run along the bank.",
    seasonalWindow:
      "The district reads best in low-angle light, when the Centre Street Bridge and the Calgary Chinese Cultural Centre roof throw long shadows across the blocks; summer evenings hold that light longest, and late-summer smoke is the main threat to contrast.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, Sien Lok Park included, so launches are staged from private property with the client's consent.",
    subjectMix: ["commercial-retail", "luxury-residential", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the base. The paperwork, not the drive, sets the lead time here — the date firms up once the authorisation is in hand.",
    faqs: [
      {
        question: "How close is Chinatown to the Calgary airport's controlled airspace?",
        answer:
          "Chinatown is inside it. The district sits about 9 km from Calgary International within the CYYC Class C control zone, and the Foothills Medical Centre heliport operates about 5 km away. Flights run under an Advanced RPAS certificate with NAV CANADA authorisation obtained before the shoot is confirmed, and every site is checked against NAV Drone.",
      },
      {
        question: "Can you photograph the Centre Street Bridge and the Cultural Centre in one flight?",
        answer:
          "Usually, yes. Chinatown is compact enough that one authorised flight plan can cover frames of the Calgary Chinese Cultural Centre and the Centre Street Bridge from the same launch point, provided both are in the plan that is filed. Adding them afterwards means a new plan, so the full shot list is set at booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Chinatown",
      "aerial photography Chinatown",
      "Chinatown drone services",
    ],
    nearestLocations: ["downtown-commercial-core", "eau-claire", "downtown-east-village", "sunnyside"],
  },

  // ──────────────────────────────────────────────────────── CLIFF BUNGALOW ──
  {
    slug: "cliff-bungalow",
    name: "Cliff Bungalow",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0364, lng: -114.0763 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Cliff Bungalow is about 11 km from Calgary International, near the CYYC control zone's charted boundary; a single address can fall either side of the line. The default plan treats it as controlled airspace until NAV Drone confirms otherwise, with authorisation filed before booking wherever it is required. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Cliff Bungalow's rooflines record its age: short blocks of low, gabled early houses interleaved with later walk-ups, on lots narrow enough that from above the district reads as fine grain against the coarser towers north of it.",
      consequence:
        "That fine grain is the product — buyers here are paying for a settled street, mature trees and the walk to 4th Street SW — and a ground-level photograph of one facade cannot show how a property sits inside it.",
      resolution:
        "A modest-altitude orbit holds the lot, its neighbours and the 4th Street SW shops and restaurants in one frame. The address is checked against the control-zone boundary first, any authorisation is filed before booking, and delivery runs in the standard turnaround.",
    },
    landmarks: [
      "4th Street SW shops and restaurants",
      "Elbow River Pathway",
      "Lindsay Park",
      "25th Avenue SW",
    ],
    terrain:
      "Level inner-city blocks off the 4th Street SW spine — small lots under a mature canopy, easing toward the Elbow River Pathway and Lindsay Park.",
    windNote:
      "Chinook country. West wind crosses the inner city with the gust spread already built in, and a calm morning can turn into a grounded afternoon inside an hour.",
    seasonalWindow:
      "Leaf-out changes this district more than most — in summer the canopy closes over the streets and frames read green from above; after leaf-fall the housing stock itself carries the picture, and low winter sun keeps the light workable through mid-day.",
    bylawNote:
      "Takeoff and landing in Calgary's municipal parks and on public pathways are restricted under the city's parks bylaws, so flights here launch from private property with the owner's consent — Lindsay Park and the river pathway stay out of the launch plan.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "Roughly 15 minutes from the base. The variable is the airspace check on the specific address, so the shot list and site are pinned down at booking.",
    faqs: [
      {
        question: "Is Cliff Bungalow inside Calgary's controlled airspace?",
        answer:
          "It sits on the line. Cliff Bungalow is about 11 km from Calgary International, near the charted edge of the CYYC control zone, so which side a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise, and any authorisation is filed before the date is booked.",
      },
      {
        question: "What does aerial photography add for a heritage-area infill in Cliff Bungalow?",
        answer:
          "Context. An infill's value here depends on how it sits among the older houses — massing, setback, the line of the street — and that judgement is only visible from above. A single orbit shows the new build inside the established block, with the 4th Street SW shops close enough to read.",
      },
    ],
    primaryKeywords: [
      "drone photography Cliff Bungalow",
      "aerial photography Cliff Bungalow",
      "Cliff Bungalow drone services",
    ],
    nearestLocations: ["mission", "erlton", "elbow-park", "beltline"],
  },

  // ────────────────────────────────────────────────────── CRESCENT HEIGHTS ──
  {
    slug: "crescent-heights",
    name: "Crescent Heights",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0596, lng: -114.0611 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "About 8 km from Calgary International, Crescent Heights falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Foothills Medical Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Crescent Heights ends in air: the street grid runs south to the Crescent Road viewpoint, then the ground drops down McHugh Bluff to the river, and from above the water — not the skyline — is what separates the district from downtown.",
      consequence:
        "A south-rim listing is priced on that outlook, but a ground camera on the lot can only point at the view — it cannot show the house holding the rim, the bluff below it and the river beyond in one picture.",
      resolution:
        "A reveal flown from street height out over the bluff line puts all three in frame — house, McHugh Bluff, water. The CYYC authorisation is filed before the date is confirmed, and the frames arrive in the standard turnaround.",
    },
    landmarks: [
      "McHugh Bluff",
      "Crescent Road viewpoint",
      "Rotary Park",
      "Centre Street North",
      "SAIT campus",
    ],
    terrain:
      "A flat-topped bench above the river valley — the grid sits level from Centre Street North across to the SAIT campus side, then the ground falls away sharply at McHugh Bluff along the southern rim.",
    windNote:
      "Chinook belt, and the rim is exposed to it. West wind that crosses the bench accelerates over the bluff edge, so conditions at the Crescent Road viewpoint can run rougher than one street back.",
    seasonalWindow:
      "The rim faces south over the valley, so it holds low winter sun well; long summer evenings put the last light on the water below the bluff, which is when the viewpoint frames earn their keep.",
    bylawNote:
      "Drone takeoff and landing in municipal parks and on pathways are restricted by Calgary's parks bylaws — Rotary Park and the bluff-top green spaces included — so launches stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the base. Same-day is realistic once the authorisation is in hand; the wind on the rim is the usual reason a date moves.",
    faqs: [
      {
        question: "What airspace applies over Crescent Heights?",
        answer:
          "The CYYC Class C control zone — the community sits about 8 km from Calgary International, with the Foothills Medical Centre heliport about 5 km away. Flights run under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the shoot is confirmed, and each site is verified against NAV Drone first.",
      },
      {
        question: "Can you shoot the view from a Crescent Road property without launching from the bluff?",
        answer:
          "Yes, and that is how it is done. McHugh Bluff and its pathways are public land, so the launch stages from the client's own property and the aircraft flies the rim from there. The frames still hold the house, the bluff and the water — the launch point never appears in them.",
      },
    ],
    primaryKeywords: [
      "drone photography Crescent Heights",
      "aerial photography Crescent Heights",
      "Crescent Heights drone services",
    ],
    nearestLocations: ["hillhurst", "sunnyside", "tuxedo-park"],
  },

  // ────────────────────────────────────────────────────── DOWNTOWN CALGARY ──
  {
    slug: "downtown-calgary",
    name: "Downtown Calgary",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0447, lng: -114.0719 },
    tier: 2,
    driveMinutesFromCalgary: 10,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "About 10 km from Calgary International, Downtown Calgary falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Foothills Medical Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Downtown Calgary runs two golden hours in the same evening: street level around Stephen Avenue Walk goes into shadow while the upper floors of The Bow tower and the top of the Calgary Tower are still carrying full sun.",
      consequence:
        "A shoot scheduled off the published sunset lands in the wrong one — either the street frames have already gone flat, or the skyline frames have passed their colour — and the difference is not recoverable in editing.",
      resolution:
        "The schedule is built to the subject's height: low work flown early, tower work held for the upper window, with a golden-hour hold where the brief calls for it. Authorisation is filed at booking and delivery runs in the standard turnaround.",
    },
    landmarks: [
      "Stephen Avenue Walk",
      "The Bow tower",
      "Calgary Tower",
      "Prince's Island Park",
      "Bow Valley Square",
    ],
    terrain:
      "Flat valley-floor ground carrying the region's tallest built relief — towers in a tight cluster, with the river and Prince's Island Park closing the core's northern side.",
    windNote:
      "Chinook belt with a tower field in it. Downdrafts and channelled gusts around the high-rises make surface readings unreliable; the working forecast is taken at flight height, and margins are kept wide.",
    seasonalWindow:
      "Winter compresses the useful window — the low sun clears the towers for only part of the day at street level — while summer evenings hold light on the upper floors long after the avenues have dimmed. Late-summer smoke is the main threat to skyline contrast.",
    bylawNote:
      "Calgary's parks bylaws restrict takeoff and landing in municipal parks and on pathways, Prince's Island Park included, so downtown launches stage from private property — a client's site, rooftop or parkade — with consent.",
    subjectMix: ["commercial-retail", "construction-progress", "municipal-infrastructure", "luxury-residential"],
    mobilisationNote:
      "Ten minutes from the base — the closest thing to zero mobilisation on the roster. Lead time here is set by the NAV CANADA authorisation, not the drive.",
    faqs: [
      {
        question: "Can drones legally fly in downtown Calgary?",
        answer:
          "Yes, with the right paperwork. Downtown sits about 10 km from Calgary International inside the CYYC Class C control zone, so flights require an Advanced RPAS certificate and a NAV CANADA authorisation, which is obtained before the shoot is confirmed. Flight plans also keep clear of the Foothills Medical Centre heliport approaches.",
      },
      {
        question: "When is the best light for a Calgary skyline shoot?",
        answer:
          "Later than the street suggests. The tower tops hold direct sun after the avenues have gone into shadow, so skyline work is scheduled to the upper window — the last stretch of the evening — while street-height frames around Stephen Avenue Walk are flown earlier. One booking can cover both if the shot list is ordered by height.",
      },
      {
        question: "How much lead time does a downtown shoot need?",
        answer:
          "Enough to file the authorisation and absorb one weather day. The drive is ten minutes; the paperwork is what sets the earliest date. Once the NAV CANADA authorisation is in hand the booking behaves like any other — wind and cloud ceiling become the only variables that move it.",
      },
    ],
    primaryKeywords: [
      "drone photography Downtown Calgary",
      "aerial photography Downtown Calgary",
      "Downtown Calgary drone services",
    ],
    nearestLocations: ["eau-claire", "beltline", "chinatown", "sunnyside"],
  },

  // ────────────────────────────────────────────── DOWNTOWN COMMERCIAL CORE ──
  {
    slug: "downtown-commercial-core",
    name: "Downtown Commercial Core",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0478, lng: -114.0693 },
    tier: 2,
    driveMinutesFromCalgary: 10,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Downtown Commercial Core sits about 9 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 5 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "On a contour map the Commercial Core is nearly blank — the walk from Olympic Plaza to Stephen Avenue Walk gains almost no elevation — yet it holds the steepest vertical in the region, every metre of it built.",
      consequence:
        "Ground photography meets that vertical from the bottom: a camera on the sidewalk shooting up a tower delivers converging lines and a sliver of sky, and it cannot show where one property stands among its neighbours.",
      resolution:
        "A frame at mid-tower height levels the geometry — an orbit that sets the subject against TD Square, The Core shopping centre and the rest of the cluster in true proportion. Authorisation precedes confirmation, and delivery runs in the standard turnaround.",
    },
    landmarks: [
      "Stephen Avenue Walk",
      "TD Square",
      "The Core shopping centre",
      "Olympic Plaza",
      "Calgary City Hall",
    ],
    terrain:
      "Level river-terrace ground under the region's densest tower cluster — the relief here is architectural, and the only open floors in the grid are Olympic Plaza and the avenue itself.",
    windNote:
      "Chinook winds meet the tower field here and come out of it disordered — accelerated in the gaps, dead behind the slabs. Flight planning treats the forecast as a starting point and the margins as the real rule.",
    seasonalWindow:
      "Street level lives in building shadow for much of the winter, which pushes low-season work toward mid-day; in summer the evening sun reaches down the east–west avenues and briefly lights the core from inside.",
    bylawNote:
      "Municipal parks and plazas are covered by Calgary's parks bylaws, which restrict drone takeoff and landing — Olympic Plaza included — so launches stage from private property such as the client's own site, with consent.",
    subjectMix: ["commercial-retail", "construction-progress", "municipal-infrastructure"],
    mobilisationNote:
      "Ten minutes from the base. The authorisation sets the earliest date; once filed, the booking moves only for weather.",
    faqs: [
      {
        question: "Is a NAV CANADA authorisation needed for the Commercial Core?",
        answer:
          "Yes. The Commercial Core sits about 9 km from Calgary International, inside the CYYC Class C control zone, so every flight runs under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed. Plans also keep clear of the Foothills Medical Centre heliport approaches nearby.",
      },
      {
        question: "How do you photograph one tower in a district full of them?",
        answer:
          "By altitude and angle, chosen in advance. An orbit at the subject's mid-height separates it from the cluster while keeping recognisable anchors — Stephen Avenue Walk, Calgary City Hall — in frame for orientation. The approach is fixed when the flight plan is filed, so the framing decisions happen at booking, not on the day.",
      },
    ],
    primaryKeywords: [
      "drone photography Downtown Commercial Core",
      "aerial photography Downtown Commercial Core",
      "Downtown Commercial Core drone services",
    ],
    nearestLocations: ["downtown-west-end", "downtown-east-village", "chinatown", "eau-claire"],
  },

  // ───────────────────────────────────────────────── DOWNTOWN EAST VILLAGE ──
  {
    slug: "downtown-east-village",
    name: "Downtown East Village",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0468, lng: -114.0559 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "Downtown East Village lies inside the charted CYYC control zone, about 9 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 6 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "East Village's organising route carries no cars: the RiverWalk runs the district's water edge past the Simmons Building, and from the air it — not the avenue grid — is the line every new building addresses.",
      consequence:
        "The pitch for a property here is its position on that pathway network — the walk to the Central Library, the river at the door — and a ground camera standing on 9th Avenue SE sees none of it.",
      resolution:
        "A rising reveal that keeps the RiverWalk and St. Patrick's Island in frame maps the property onto the network buyers are actually buying. The authorisation is filed before the date is confirmed; delivery is in the standard turnaround.",
    },
    landmarks: [
      "RiverWalk",
      "Central Library",
      "St. Patrick's Island",
      "Simmons Building",
      "9th Avenue SE",
    ],
    terrain:
      "Flat riverbank ground at the core's eastern edge — a rebuilt district of new blocks along the water, with St. Patrick's Island sitting just offshore and the land opening east toward Fort Calgary.",
    windNote:
      "Chinook belt. The district sits at the open east end of the tower core, so westerly gusts arrive here after crossing downtown — broken, shifty and stronger than the street-level feel suggests.",
    seasonalWindow:
      "The water carries the light here: ice-off and full flow put moving texture beside every riverside frame, and long summer evenings light the RiverWalk edge after the core behind it has dimmed. Smoke season is the main threat to late-summer contrast.",
    bylawNote:
      "Calgary's parks bylaws restrict takeoff and landing in municipal parks and on pathways — the RiverWalk and St. Patrick's Island included — so launches stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "municipal-infrastructure", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base. Authorisation lead time governs the calendar; the site itself is minutes away once the paperwork clears.",
    faqs: [
      {
        question: "Does the East Village need its own airspace authorisation?",
        answer:
          "Yes — the district is inside the CYYC Class C control zone, about 9 km from Calgary International, with the Foothills Medical Centre heliport roughly 6 km away. Flights are conducted under an Advanced RPAS certificate with NAV CANADA authorisation secured before the date is confirmed, and the site is verified on NAV Drone.",
      },
      {
        question: "Can a condo shoot here show the RiverWalk without flying over it?",
        answer:
          "Yes. The pathway hugs the bank, so an orbit flown over the property side of the block holds the RiverWalk, the water and St. Patrick's Island in frame from a respectful offset. The pathway and the island are public spaces, so they stay out of the launch plan entirely.",
      },
    ],
    primaryKeywords: [
      "drone photography Downtown East Village",
      "aerial photography Downtown East Village",
      "Downtown East Village drone services",
    ],
    nearestLocations: ["downtown-commercial-core", "chinatown", "inglewood", "bridgeland-riverside"],
  },

  // ───────────────────────────────────────────────────── DOWNTOWN WEST END ──
  {
    slug: "downtown-west-end",
    name: "Downtown West End",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0483, lng: -114.0868 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 10,
      authorizationRequired: true,
      authorizationNote:
        "Downtown West End sits about 10 km from Calgary International, inside the CYYC control zone as charted. Flights here are conducted under an Advanced RPAS certificate with a NAV CANADA RPAS authorisation obtained before the shoot is confirmed. Foothills Medical Centre heliport operates about 4 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The West End is downtown's windward edge: a chinook that crosses the city meets the tower mass here first, and the river corridor along the Bow River Pathway hands the west wind a straight run into the district.",
      consequence:
        "Flight windows here are shorter and shiftier than a city-average forecast implies — a day that is flyable in the east core can be grounded at 14th Street SW, and the difference only shows up on site.",
      resolution:
        "West End shoots are booked with a wind margin and a named backup window, gusts are read at flight height before launch, and the authorised plan is flown when the corridor goes quiet — delivered in the standard turnaround.",
    },
    landmarks: [
      "Bow River Pathway",
      "Shaw Millennium Park",
      "Sunalta LRT Station",
      "14th Street SW",
      "Bow Trail",
    ],
    terrain:
      "Flat ground where the tower core thins westward into lower blocks — the river runs along the district's north side and Bow Trail feeds in from the west.",
    windNote:
      "The most wind-exposed corner of the core. Chinook westerlies arrive unbroken off the open ground west of the city and funnel along the river corridor, so gust spread — not average speed — is the number that decides a launch.",
    seasonalWindow:
      "Winter chinooks make this the core's most volatile scheduling zone, with clear, warm afternoons that are unflyable; calm summer mornings are the district's most reliable light, before the westerly builds.",
    bylawNote:
      "Under Calgary's parks bylaws, municipal parks and pathways — Shaw Millennium Park and the river pathway among them — are not available for drone takeoff or landing, so launches stage from private property with consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base — close enough to take a short-notice calm window the same day, once the authorisation is already in hand.",
    faqs: [
      {
        question: "What are the airspace rules for the Downtown West End?",
        answer:
          "The district is inside the CYYC Class C control zone, about 10 km from Calgary International, and the Foothills Medical Centre heliport operates roughly 4 km away. Flights run under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the shoot is confirmed, and every plan keeps clear of the heliport approaches.",
      },
      {
        question: "Why would a West End shoot be postponed when downtown looks calm?",
        answer:
          "Because the wind arrives here first. The West End takes the westerly before the rest of the core and the river corridor accelerates it, so conditions at Shaw Millennium Park can be out of limits while the east core is flyable. The booking carries a backup window for exactly that case.",
      },
    ],
    primaryKeywords: [
      "drone photography Downtown West End",
      "aerial photography Downtown West End",
      "Downtown West End drone services",
    ],
    nearestLocations: ["downtown-commercial-core", "beltline", "eau-claire"],
  },

  // ──────────────────────────────────────────────────────────── EAU CLAIRE ──
  {
    slug: "eau-claire",
    name: "Eau Claire",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0498, lng: -114.0765 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, Eau Claire falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Foothills Medical Centre heliport operates about 4 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Eau Claire is where downtown's height runs out: the rooflines step down block by block until the Peace Bridge crosses at water level, and Prince's Island Park sits lower still, in the river itself.",
      consequence:
        "That descent is the district's whole appeal — towers behind, water and park in front — and no ground camera can stand anywhere that shows both sides of the step at once.",
      resolution:
        "An orbit at mid height holds the falling roofline, Eau Claire Plaza and the island in one frame, downtown to water in a single read. Authorisation is filed before the date is set, and the work is delivered in the standard turnaround.",
    },
    landmarks: [
      "Prince's Island Park",
      "Eau Claire Plaza",
      "Peace Bridge",
      "Bow River Pathway",
      "Riverfront Avenue",
    ],
    terrain:
      "The flat seam between the tower core and the river — plaza and low blocks giving way to the Bow River Pathway, with Prince's Island Park lying in the channel just offshore.",
    windNote:
      "Chinook belt, on the river's line. Westerlies follow the water through here, and the last of the tower field to the south leaves the gusts uneven — margins are set for the corridor, not the city average.",
    seasonalWindow:
      "Ice-off restores moving water to every frame and marks the start of the district's best season; long summer evenings light the Peace Bridge and the island after the core has gone to shadow.",
    bylawNote:
      "Prince's Island Park and the river pathway are municipal spaces where Calgary's parks bylaws restrict drone takeoff and landing, so flights here launch from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "Roughly 15 minutes from the base. The calendar is governed by the authorisation; once it is filed, light and wind pick the hour.",
    faqs: [
      {
        question: "Do Eau Claire drone flights need NAV CANADA approval?",
        answer:
          "Yes. Eau Claire sits about 9 km from Calgary International inside the CYYC Class C control zone, with the Foothills Medical Centre heliport roughly 4 km away. Every flight runs under an Advanced RPAS certificate with the NAV CANADA authorisation obtained before the shoot is confirmed, and the site is checked on NAV Drone.",
      },
      {
        question: "Can one flight capture both the towers and Prince's Island Park?",
        answer:
          "Yes — that pairing is the reason to fly here. The step from tower core to river is short, so a single authorised plan can hold the skyline edge, Eau Claire Plaza, the Peace Bridge and the island in successive frames without repositioning the launch. The full sequence is set in the plan at booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Eau Claire",
      "aerial photography Eau Claire",
      "Eau Claire drone services",
    ],
    nearestLocations: ["downtown-calgary", "sunnyside", "chinatown"],
  },

  // ──────────────────────────────────────────────────────────── ELBOW PARK ──
  {
    slug: "elbow-park",
    name: "Elbow Park",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0233, lng: -114.0827 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 12,
      authorizationRequired: true,
      authorizationNote:
        "Elbow Park sits about 12 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Elbow Park's streets take their orders from the river: Elbow Drive SW and Sifton Boulevard SW bend with the water instead of the survey grid, and from above the district reads as a set of long curves in a city built on right angles.",
      consequence:
        "Those curves are the address — the geometry is what buyers at this end of the market are paying into — yet at ground level a curving street photographs exactly like a straight one.",
      resolution:
        "A climb to the height where the bend of Elbow Drive SW and the river line register puts the geometry back in the picture with the property placed on it. The address is planned as controlled airspace until NAV Drone shows otherwise, and delivery runs in the standard turnaround.",
    },
    landmarks: [
      "Elbow Drive SW",
      "Sifton Boulevard SW",
      "Stanley Park",
      "River Park",
      "Elbow River Pathway",
    ],
    terrain:
      "Low, treed river flats inside the Elbow's curves, with River Park and Stanley Park holding the banks and the ground rising gently away from the water.",
    windNote:
      "Chinook country. The mature canopy on the river flats softens what reaches the streets, but the wind above the treetops is the wind the aircraft flies in, and it carries the usual chinook gust spread.",
    seasonalWindow:
      "In leaf the canopy closes over whole blocks and the district photographs as parkland with rooflines; after leaf-fall the houses and the curving plan come forward, and low winter sun through bare trees is the season's particular reward.",
    bylawNote:
      "Stanley Park, River Park and the river pathway fall under Calgary's parks bylaws, which restrict drone takeoff and landing on municipal land — so launches stage from the client's own property, with consent.",
    subjectMix: ["luxury-residential", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base. The airspace check on the exact address is the one item that must precede the date; after that, scheduling is straightforward.",
    faqs: [
      {
        question: "Is Elbow Park in controlled airspace?",
        answer:
          "It sits near the boundary. Elbow Park is about 12 km from Calgary International, close to the charted edge of the CYYC control zone, so the specific address determines the paperwork. Every site is planned as controlled until NAV Drone shows otherwise, and where authorisation is required it is filed before the date is booked.",
      },
      {
        question: "What does an aerial add for an Elbow Park estate listing?",
        answer:
          "The plan of the place. The curving streets, the river line and the tree cover are the district's signature, and none of it survives a ground-level photograph. One orbit places the house on Elbow Drive SW's bend with Stanley Park and the water in frame — the setting priced into the address, made visible.",
      },
    ],
    primaryKeywords: [
      "drone photography Elbow Park",
      "aerial photography Elbow Park",
      "Elbow Park drone services",
    ],
    nearestLocations: ["mission", "cliff-bungalow", "erlton"],
  },

  // ──────────────────────────────────────────────────────────────── ERLTON ──
  {
    slug: "erlton",
    name: "Erlton",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0295, lng: -114.0691 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Erlton sits about 11 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "The Elbow does Erlton's framing for it: the river runs the community's edge with the Elbow River Pathway tracing the bank, and Stampede Park faces the houses from the far side of the water.",
      consequence:
        "A property here holds two stories at once — quiet river frontage and front-row Stampede adjacency — and street-level photography, boxed in by its own block, tells neither.",
      resolution:
        "A boundary-line orbit flown along the river's line carries both banks in frame: the property, the pathway, the water and Stampede Park beyond. The address is confirmed against NAV Drone first, any required authorisation is filed before booking, and delivery is in the standard turnaround.",
    },
    landmarks: [
      "Stampede Park",
      "Elbow River Pathway",
      "Lindsay Park (Repsol Sport Centre)",
      "Erlton Street SW",
    ],
    terrain:
      "River-flat ground held in the Elbow's curve — a small residential pocket with the water on one side, Lindsay Park across it, and the Stampede grounds opening out on the far bank.",
    windNote:
      "Chinook belt. The valley notch the river cuts here is shallow, so it offers little shelter — the west wind crosses the flats much as it crosses the rest of the inner city, gust spread included.",
    seasonalWindow:
      "Ice-off puts moving water back into every riverside frame, and summer evenings light the far bank; during Stampede season the far side of the river becomes its own subject, and shoot timing accounts for the activity around the grounds.",
    bylawNote:
      "The river pathway and municipal parkland along the Elbow are covered by Calgary's parks bylaws, which restrict drone takeoff and landing there — launches stage from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "municipal-infrastructure"],
    mobilisationNote:
      "About 15 minutes from the base. Confirming which side of the control-zone line the address falls on is step one; the drive is never the constraint.",
    faqs: [
      {
        question: "Does flying a drone in Erlton require authorisation?",
        answer:
          "Sometimes — Erlton sits about 11 km from Calgary International, near the charted boundary of the CYYC control zone, and which side of the line an address falls on decides the paperwork. Each site is planned as controlled airspace until NAV Drone shows otherwise, with any authorisation filed before the date is booked.",
      },
      {
        question: "Can you photograph a property with Stampede Park in the background?",
        answer:
          "Yes. The grounds sit directly across the river from Erlton, so an orbit flown on the property's side of the Elbow holds the house, the water and Stampede Park in one frame without overflying the grounds themselves. The angle is set in the flight plan, so the pairing is deliberate rather than lucky.",
      },
    ],
    primaryKeywords: [
      "drone photography Erlton",
      "aerial photography Erlton",
      "Erlton drone services",
    ],
    nearestLocations: ["mission", "cliff-bungalow", "downtown-calgary"],
  },

  // ───────────────────────────────────────────────────────────── HILLHURST ──
  {
    slug: "hillhurst",
    name: "Hillhurst",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0596, lng: -114.0954 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, Hillhurst falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 3 km away. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Hillhurst is a slope wearing a grid: the ground climbs from the flats around the Kensington Village shops toward the SAIT campus on the hill above, and each east–west street sits a little higher than the one below it.",
      consequence:
        "On the ground that climb disappears — every block looks level from inside it — so a listing on the upper streets cannot show the position, and the outlook, that separates it from the flats below.",
      resolution:
        "A rising reveal from street height reads the slope in one continuous move, placing the property above Riley Park with the district falling away below it. The CYYC authorisation is filed before the date is confirmed, and the frames arrive in the standard turnaround.",
    },
    landmarks: [
      "Kensington Village shops",
      "Riley Park",
      "SAIT campus",
      "5th Avenue NW",
      "Kensington Road NW",
    ],
    terrain:
      "A residential grid laid over the rise from the river flats to the hill's shoulder — level near Kensington Road NW, climbing steadily north, with Riley Park holding the green space on the lower ground.",
    windNote:
      "Chinook country. Nothing on the open rise breaks the west wind, and readings taken on the flats near the shops can understate what is running across the upper streets.",
    seasonalWindow:
      "The south-facing slope holds low winter sun better than the flats it overlooks, and long summer evenings rake light across the climb — the hour that shows the grade is the hour worth booking.",
    bylawNote:
      "Calgary's parks bylaws restrict drone takeoff and landing in municipal parks and on pathways, with Riley Park no exception, so Hillhurst launches stage from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the base. With the heliport close by, the flight plan is settled at booking — after that, only weather moves the date.",
    faqs: [
      {
        question: "What should Hillhurst clients know about the airspace?",
        answer:
          "Two things sit close: the community is about 9 km from Calgary International inside the CYYC Class C control zone, and the Foothills Medical Centre heliport operates roughly 3 km away. Flights run under an Advanced RPAS certificate with NAV CANADA authorisation filed before confirmation, and plans keep clear of the heliport approaches.",
      },
      {
        question: "Why does elevation matter for a Hillhurst listing?",
        answer:
          "Because the district is a slope, and position on it is value. Upper-street homes stand above the flats with an outlook the lower blocks do not have, yet a ground photograph makes every street look the same. An aerial reveal shows exactly where on the climb the property sits.",
      },
    ],
    primaryKeywords: [
      "drone photography Hillhurst",
      "aerial photography Hillhurst",
      "Hillhurst drone services",
    ],
    nearestLocations: ["sunnyside", "west-hillhurst", "capitol-hill"],
  },
// ─────────────────────────────────────────────────────────────── MISSION ──
  {
    slug: "mission",
    name: "Mission",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0335, lng: -114.0745 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "CYYC control zone edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 11,
      authorizationRequired: true,
      authorizationNote:
        "Mission sits about 11 km from Calgary International — near the charted boundary of the CYYC control zone, close enough that which side of the line a given address falls on decides the paperwork. It is planned as controlled airspace until NAV Drone shows otherwise for the specific site, and where authorisation is required it is filed before the date is booked. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "At golden hour the brightest thing in Mission is the water — the low sun catches the Elbow River where it bends around Lindsay Park (Repsol Sport Centre), and from the air the river reads as a lit ribbon while the streets below have already gone soft.",
      consequence:
        "That window is short, and it is the frame that sells the address. A ground photographer working the same hour gets dim façades under a sky that has outrun the street — the river, the reason the listing mentions the pathway at all, never makes the picture.",
      resolution:
        "A golden-hour hold: the flight is scheduled against the last-light window and holds altitude while the water carries the light, with stills and video delivered in the standard turnaround.",
    },
    landmarks: [
      "4th Street SW",
      "17th Avenue SW",
      "Elbow River Pathway",
      "Lindsay Park (Repsol Sport Centre)",
      "Cathedral of the Redeemer",
    ],
    terrain:
      "Low river flat on the inside of an Elbow River bend — level block by block, with the water wrapping the community's east and south edges and the ground rising away to the west.",
    windNote:
      "Chinook belt. The low ground beside the Elbow takes a margin off the west wind, but on a chinook day the gust spread, not the average, still sets the flight window.",
    seasonalWindow:
      "Open water carries the community's best frames, so ice-off to freeze-up is the working season for river shots; in deep winter the frame shifts to snow geometry and the warm dusk light of the 4th Street SW blocks.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone takeoff and landing in municipal parks and pathways, so Mission launches are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Same-day is realistic once authorisation is in hand, and a booking pairs naturally with Cliff Bungalow, Erlton or the Beltline next door.",
    faqs: [
      {
        question: "Is Mission inside the Calgary airport control zone?",
        answer:
          "It sits close to the charted boundary — about 11 km from Calgary International — and which side of the line a specific address falls on decides the paperwork. Fly4MEdia plans Mission as controlled airspace until NAV Drone shows otherwise, and files any required authorisation before your shoot date is confirmed.",
      },
      {
        question: "When is the best time of day for aerial photography in Mission?",
        answer:
          "The last hour of light. Mission's strongest frames put the Elbow River in the picture, and low sun turns the water into the brightest surface in the scene while the streets soften. The flight is scheduled against that window rather than squeezed into a midday slot.",
      },
      {
        question: "Can a drone shoot show a Mission condo's position on the river?",
        answer:
          "Yes — that is the frame Mission listings usually need. An aerial places the building against the Elbow River Pathway, the 4th Street SW blocks and Lindsay Park in a single image, so a buyer sees the setting a balcony photo can only hint at.",
      },
    ],
    primaryKeywords: [
      "drone photography Mission",
      "aerial photography Mission",
      "Mission drone services",
    ],
    nearestLocations: ["cliff-bungalow", "erlton", "beltline", "elbow-park"],
  },

  // ────────────────────────────────────────────────────────────── ROSEDALE ──
  {
    slug: "rosedale",
    name: "Rosedale",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0611, lng: -114.0708 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 8,
      authorizationRequired: true,
      authorizationNote:
        "Rosedale lies inside the charted CYYC control zone, about 8 km from Calgary International. Work here flies under an Advanced RPAS certificate, and the NAV CANADA RPAS authorisation is in hand before a shoot date is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 4 km away. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Crescent Road is a one-sided street — houses along its north edge, and on the south nothing but the drop of McHugh Bluff, so from above Rosedale's last row of rooflines and the city skyline share a single frame.",
      consequence:
        "That relationship is the whole premium, and ground photography cannot hold both ends of it. Shot from the street you get the house or you get the view from the escarpment viewpoint — never the fact that the address commands it.",
      resolution:
        "A boundary-line orbit flown off the bluff face keeps the home, the drop and the skyline in one continuous move, delivered in the standard turnaround.",
    },
    landmarks: [
      "Crescent Road escarpment viewpoint",
      "McHugh Bluff",
      "Crescent Road",
      "Rosedale School",
      "5A Street NW",
    ],
    terrain:
      "A small plateau community on the crown of the escarpment — level, mature-canopy streets behind Crescent Road, with McHugh Bluff dropping away to the river flat below.",
    windNote:
      "Chinook belt, and exposed with it. The crown of the escarpment takes the west wind without shelter, and Crescent Road often records gusts the flat streets behind it do not.",
    seasonalWindow:
      "The bluff face takes the winter sun, so snow burns off the drop while the level streets behind hold it; clear shoulder-season evenings put the day's lowest light on the skyline beyond the viewpoint.",
    bylawNote:
      "Calgary's parks bylaws prohibit drone launch and recovery in municipal parks and on pathways — the bluff and the viewpoint included — so flights stage from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Comfortably same-day once authorisation is filed, and a booking pairs naturally with Sunnyside, Crescent Heights or Hillhurst.",
    faqs: [
      {
        question: "What does flying a drone in Rosedale legally involve?",
        answer:
          "Rosedale is inside the Calgary International control zone, about 8 km from the airport, so flights run under an Advanced RPAS certificate with a NAV CANADA authorisation filed before the date is confirmed. The Foothills Medical Centre heliport operates about 4 km away, and flight plans keep clear of its approaches.",
      },
      {
        question: "Why photograph a Rosedale home from the air instead of from the viewpoint?",
        answer:
          "Because the viewpoint looks away from the houses. A photograph taken from Crescent Road shows the skyline or the home, not both. An aerial off the bluff turns around and holds the roofline, McHugh Bluff and downtown in one frame — the exact reason the street carries its price.",
      },
    ],
    primaryKeywords: [
      "drone photography Rosedale",
      "aerial photography Rosedale",
      "Rosedale drone services",
    ],
    nearestLocations: ["sunnyside", "crescent-heights", "hillhurst", "mount-pleasant"],
  },

  // ───────────────────────────────────────────────────────────── SUNNYSIDE ──
  {
    slug: "sunnyside",
    name: "Sunnyside",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.059, lng: -114.0866 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, Sunnyside falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Foothills Medical Centre heliport operates about 3 km away, and flight plans keep clear of its approaches. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Sunnyside keeps two calendars — in full leaf the canopy closes over Fifth Avenue NW and, from the air, the blocks between Riley Park and the Bow River Pathway read as one continuous stand of green; after leaf-fall the rooflines come back.",
      consequence:
        "So the shoot date decides what the picture proves. A summer aerial sells the leafy setting and hides the house; a bare-season aerial documents the roofline and the lot but loses the character. Book without weighing that and you get the wrong one.",
      resolution:
        "The flight date is matched to the job — foliage for marketing frames, shoulder season for a progress series or a roof-and-lot record — and delivered in the standard turnaround.",
    },
    landmarks: [
      "Sunnyside LRT Station",
      "Kensington Road NW shops",
      "Riley Park",
      "Bow River Pathway",
      "Fifth Avenue NW",
    ],
    terrain:
      "River flat between the Bow and the bluff rising to the north — level ground, small lots, and a mature canopy that reads as continuous green from above in summer.",
    windNote:
      "Chinook belt. The flat takes modest shelter from the bluff on a north wind, but the west wind that matters here runs the Bow corridor with little to slow it.",
    seasonalWindow:
      "The canopy sets the calendar: full leaf closes over the avenues from late spring to the first hard frosts, and the weeks either side — leaf-out and leaf-fall — are when rooflines and tree cover share the frame.",
    bylawNote:
      "City of Calgary bylaws restrict drone takeoff and landing in parks and along pathways, Riley Park and the riverfront included, so launches are arranged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Same-day is realistic once authorisation is filed, and a booking pairs naturally with Hillhurst, Eau Claire or downtown across the river.",
    faqs: [
      {
        question: "Do Sunnyside drone shoots need NAV CANADA sign-off?",
        answer:
          "Yes. Sunnyside sits about 9 km from Calgary International, inside the CYYC Class C control zone, so flights operate under an Advanced RPAS certificate with authorisation filed before the shoot is confirmed. The Foothills Medical Centre heliport works about 3 km away, and plans keep clear of its approaches.",
      },
      {
        question: "What season should a Sunnyside property be photographed from the air?",
        answer:
          "It depends on what the frame must show. In leaf, the mature canopy reads as parkland running to the Bow River Pathway — strong for marketing, poor for showing the building. Leaf-out and leaf-fall put roofline and greenery in the same image, which suits most listings best.",
      },
    ],
    primaryKeywords: [
      "drone photography Sunnyside",
      "aerial photography Sunnyside",
      "Sunnyside drone services",
    ],
    nearestLocations: ["hillhurst", "eau-claire", "downtown-calgary"],
  },

  // ─────────────────────────────────────────────────────────── TUXEDO PARK ──
  {
    slug: "tuxedo-park",
    name: "Tuxedo Park",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0662, lng: -114.0574 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 7,
      authorizationRequired: true,
      authorizationNote:
        "About 7 km from Calgary International, Tuxedo Park falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Foothills Medical Centre heliport operates about 5 km away, and flight plans keep clear of its approaches. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Tuxedo Park is flat in a way a photographer feels at once — the grid either side of Centre Street North holds level block after block, and the nearest real drop lies off the community's edge, where the ground falls toward the Deerfoot Trail corridor.",
      consequence:
        "Flat ground offers no free vantage. There is no hill, no bluff, no upper storey that shows how a property sits against the park, the hall or downtown — every ground photograph here is taken from the same eye level the buyer already knows.",
      resolution:
        "Altitude manufactures the vantage the terrain refuses: a rising reveal over the property places it against Tuxedo Park and the skyline beyond, delivered in the standard turnaround.",
    },
    landmarks: [
      "Tuxedo Park Community Hall",
      "Tuxedo Park",
      "Centre Street North",
      "Confederation Park (nearby)",
    ],
    terrain:
      "A level grid on the plateau north of downtown — the blocks hold flat across the community, with the ground easing away east toward the Deerfoot Trail corridor and green space at Confederation Park nearby.",
    windNote:
      "Chinook belt with no terrain to break it. The plateau grid takes the west wind evenly, so the gust spread in the forecast, not the average, decides the flight window.",
    seasonalWindow:
      "Flat ground means the light does the modelling. Low-angle winter sun rakes the grid cleanly, and after a chinook the plateau's snow strips fast and evenly, resetting the frame from winter to bare in days.",
    bylawNote:
      "Municipal parks and pathways are off-limits for drone takeoff and landing under City of Calgary bylaws, so launches here stage from private property with the owner's permission.",
    subjectMix: ["luxury-residential", "commercial-retail", "construction-progress"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Same-day is realistic once authorisation is in hand, and a booking pairs naturally with Mount Pleasant, Capitol Hill or Crescent Heights.",
    faqs: [
      {
        question: "Tuxedo Park sits 7 km from the airport — is drone work still possible?",
        answer:
          "Yes, with the right paperwork. Tuxedo Park is inside the CYYC Class C control zone, so flights operate under an Advanced RPAS certificate with a NAV CANADA authorisation obtained before the date is confirmed. The exact site and altitude are checked against NAV Drone before every flight.",
      },
      {
        question: "Why bother with aerial photography on ground as flat as Tuxedo Park's?",
        answer:
          "Because flat ground is exactly where elevation earns its keep. With no natural vantage anywhere in the community, an aerial is the only image that shows a lot's position relative to Tuxedo Park, Centre Street North and the skyline — context no ground photograph here can supply.",
      },
    ],
    primaryKeywords: [
      "drone photography Tuxedo Park",
      "aerial photography Tuxedo Park",
      "Tuxedo Park drone services",
    ],
    nearestLocations: ["capitol-hill", "mount-pleasant", "crescent-heights", "banff-trail"],
  },

  // ──────────────────────────────────────────────────────── WEST HILLHURST ──
  {
    slug: "west-hillhurst",
    name: "West Hillhurst",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0625, lng: -114.1078 },
    tier: 2,
    driveMinutesFromCalgary: 15,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 9,
      authorizationRequired: true,
      authorizationNote:
        "About 9 km from Calgary International, West Hillhurst falls inside the CYYC control zone as charted. An Advanced RPAS certificate covers the operation, with the NAV CANADA RPAS authorisation secured before the booking is confirmed. Flight plans here also keep clear of Foothills Medical Centre heliport, operating about 2 km away. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
      restrictedZones: ["Calgary International Airport (CYYC) approach paths","Foothills Medical Centre heliport"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "From altitude West Hillhurst's plan explains itself — the avenues run parallel to the Bow, Kensington Road NW carries the commerce along the southern blocks, and 19th Street NW stitches the grid down to the Bow River Pathway at the water.",
      consequence:
        "Listings here trade on that geometry — a block to the shops, a short walk to the pathway — and ground photography can assert it but never show it. The buyer is asked to take the map's word for the thing the pictures should prove.",
      resolution:
        "An approach run flown along the connecting street traces the actual route from the front door to the river in one continuous move, delivered in the standard turnaround.",
    },
    landmarks: [
      "Helicopter Park",
      "Foothills Athletic Park",
      "Bow River Pathway",
      "West Hillhurst Community Centre",
      "19th Street NW",
    ],
    terrain:
      "Level river flat along the Bow, with the grid stepping gently uphill to the north and the open ground of Foothills Athletic Park and Helicopter Park breaking the rooflines.",
    windNote:
      "Chinook belt. West wind arrives along the Bow corridor with little to slow it, and the open ground at Foothills Athletic Park gives an honest read of conditions before launch.",
    seasonalWindow:
      "The river flat greens early and holds it. Open water on the Bow gives reflected light at both ends of the day through most of the year, and the athletic fields read green well into fall.",
    bylawNote:
      "City of Calgary parks bylaws restrict drone launch and recovery in municipal parks and pathways — Helicopter Park included, despite the name — so flights are staged from private property with the client's consent.",
    subjectMix: ["luxury-residential", "construction-progress", "commercial-retail"],
    mobilisationNote:
      "About 15 minutes from the Calgary base. Same-day is realistic once authorisation is filed, and a booking pairs naturally with Hillhurst, Parkdale or Capitol Hill.",
    faqs: [
      {
        question: "Can drones operate in West Hillhurst with the Foothills heliport so close?",
        answer:
          "Yes, with planning. The Foothills Medical Centre heliport operates about 2 km away, so flight plans keep clear of its approaches, and West Hillhurst itself sits inside the CYYC control zone — flights run under an Advanced RPAS certificate with NAV CANADA authorisation filed before the date is confirmed.",
      },
      {
        question: "Can an aerial actually show how close a West Hillhurst home is to the river?",
        answer:
          "Yes — that is the point of flying here. A single frame can hold the property, the run of avenues and the Bow River Pathway, and a short video traces the walk itself. Distance claims stop being copy and become something the buyer has watched.",
      },
      {
        question: "Is Helicopter Park a place you can launch a drone from?",
        answer:
          "No — the name belongs to a neighbourhood park, not an aerodrome. City of Calgary bylaws restrict takeoff and landing in municipal parks, Helicopter Park included, so launches stage from private property with the client's consent. The park can still appear in frames; it just is not the launch site.",
      },
    ],
    primaryKeywords: [
      "drone photography West Hillhurst",
      "aerial photography West Hillhurst",
      "West Hillhurst drone services",
    ],
    nearestLocations: ["hillhurst", "parkdale", "capitol-hill"],
  }
];
